'use client';

import { useTheme } from '@mui/material';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useLeftSidebarStore } from '@efacity/frontend-next-shared/header';
import { adminsTeachersAndIndependentRoles, AuthState, hasAnyOfRoles, Roles } from '@efacity/common';
import { useOrganizationRegionsOptions } from '@efacity/frontend-shared';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Header } from '@efacity/frontend-next-shared/header';
import { Content } from './Content';
import { LeftNavigation } from './LeftNavigation';
import { LeftNavigationItems } from './LeftNavigationItems';
import { NavigateItemNext } from './NavigateItemNext';
import { NavigationItemReact } from './NavigationItemReact';
import { NavigationItem } from './leftNavigationHelpers';
import { OrganizationInformation } from '@efacity/react-next-sc';

export interface MainContentProps {
  orgId: string | undefined;
  authState: AuthState;
  children: ReactNode;
  renderNavigationItem?: (navigationItem: NavigationItem, key: string) => ReactElement;
}

const renderNavigationItemNext = (navigationItem: NavigationItem, key: string) => {
  return <NavigateItemNext key={key} navigationItem={navigationItem} />;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renderNavigationItemReact = (navigationItem: NavigationItem, key: string) => {
  return <NavigationItemReact key={key} navigationItem={navigationItem} />;
};

export function ContentWithLeftNavigation({
  orgId,
  authState,
  children,
  renderNavigationItem = renderNavigationItemNext
}: MainContentProps) {
  const setIsExpanded = useLeftSidebarStore((state) => state.setLeftNavExpanded);
  const isExpanded = useLeftSidebarStore((state) => state.isExpanded);

  const { organizationRegions } = useOrganizationRegionsOptions(orgId);

  const showLeftNavigation = hasAnyOfRoles(
    [...adminsTeachersAndIndependentRoles, Roles.SiteAdmin],
    authState?.user?.roles || []
  );
  const theme = useTheme();
  const showPermanentDrawer = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    setIsExpanded(showLeftNavigation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMainContentAreaClick = () => {
    if (isExpanded && !showPermanentDrawer) setIsExpanded(false);
  };

  return (
    <div data-testid="mainLayout">
      {/*
          TODO: Header should be improved to avoid content change
          currently it's showing sign in buttons first and then current user info
        */}
      <Header showLeftNavigationButton={showLeftNavigation} orgInfo={organizationRegions as OrganizationInformation} />
      <div>
        {showLeftNavigation && (
          <LeftNavigation>
            <LeftNavigationItems
              authState={authState}
              currentOrgId={orgId}
              enrollmentRequired={organizationRegions.enrollmentRequired}
              renderNavigationItem={renderNavigationItem}
            />
          </LeftNavigation>
        )}
        <Content onMainContentClick={handleMainContentAreaClick}>{children}</Content>
      </div>
    </div>
  );
}
